import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Plus, Search, Save, Archive, ArchiveRestore, Tag,
  Pencil, Download, X, RotateCcw, Percent, LockKeyhole, ChevronDown,
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import SidePanel from "@/components/calendar/SidePanel";
import {
  procedureColor, buildProcedureColorMap, CATEGORY_HUES,
} from "@/components/treatment/procedureColors";
import {
  APPROVED_PROCEDURE_CATEGORIES,
  isApprovedProcedureCategory,
} from "@/lib/procedureCategories";

// =====================================================================
// Settings → Services (V2 Round 4 follow-on)
//
// Procedure catalog editor. Lists procedures grouped by category with
// fee + duration. Click a row opens a slide-in panel for edit; +Add
// opens the same panel in new mode. Built-in services stay available
// because charting and treatment planning depend on their metadata.
// Clinic-added services can be archived without breaking historical
// money_events or treatment_plan_items references.
//
// Calls:
//   list_procedures_for_settings(p_include_archived)
//   create_procedure / update_procedure
//   archive_procedure / unarchive_procedure
// =====================================================================

const CURRENCY_SYMBOL = "₱";
const DEFAULT_DURATIONS = [10, 15, 20, 30, 45, 60, 75, 90, 120];
const DRAWER_CONTROL_CLASS = "min-h-11 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100";
const DRAWER_CHECKBOX_CLASS = "mt-0.5 h-5 w-5 shrink-0 rounded border-slate-300 text-teal-600 focus:ring-teal-300";
const ORTHO_VISIT_LINK_OPTIONS = [
  { value: "", label: "Not linked to Ortho" },
  { value: "bonding", label: "Bonding visit" },
  { value: "adjustment", label: "Adjustment visit" },
  { value: "debonding", label: "Debonding visit" },
  { value: "retention", label: "Retention check" },
  { value: "emergency", label: "Emergency ortho visit" },
];
const CREATE_POST_OP_PROCEDURE_VALUE = "__create_post_op_procedure__";
const DEFAULT_POST_OP_PROCEDURE_INPUT = {
  name: "Post-Op",
  short_code: "POSTOP",
  code: null,
  category: "Follow-up",
  default_fee_centavos: 0,
  suggested_duration_minutes: 10,
  unit_basis: "fixed",
  uses_chair_setup: true,
  multi_visit: false,
  default_booking_window_days: null,
  tooth_specific: false,
  surface_specific: false,
  additional_surface_pct: null,
  is_extraction: false,
  clears_calculus_deposits: false,
  resolves_caries_findings: false,
  tooth_count: "none",
  multi_tooth_kind: null,
  tooth_role_required: false,
  requires_lab_work: false,
  ortho_visit_type: null,
  visit_count: 1,
  default_visit_template: [],
  notes: "Post-operative follow-up check. Editable per clinic.",
};

const MULTI_TOOTH_KIND_LABELS = {
  bridge: "Bridge",
  partial_denture: "Partial Denture",
  full_denture: "Full Denture",
  crown_unit: "Crown Units",
};

function multiToothKindLabel(kind) {
  return MULTI_TOOTH_KIND_LABELS[kind] || null;
}

export default function SettingsServices() {
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [rows, setRows]         = useState([]);
  const [search, setSearch]     = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [panel, setPanel]       = useState(null); // null | { mode:'new' } | { mode:'edit', row }
  const [importing, setImporting] = useState(false);
  const [colorPicker, setColorPicker] = useState(null); // null | row
  const [inlinePriceEditId, setInlinePriceEditId] = useState(null);
  const [inlinePriceDraft, setInlinePriceDraft] = useState("");
  const [inlinePriceBusyId, setInlinePriceBusyId] = useState(null);
  const [priceErrors, setPriceErrors] = useState({});
  const [bulkEditing, setBulkEditing] = useState(false);
  const [bulkDrafts, setBulkDrafts] = useState({});
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkError, setBulkError] = useState(null);
  const [adjustPopoverCategory, setAdjustPopoverCategory] = useState(null);
  const [adjustDirection, setAdjustDirection] = useState("+");
  const [adjustPercent, setAdjustPercent] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const { data, error: e } = await supabase.rpc("list_procedures_for_settings", {
      p_include_archived: showArchived,
    });
    setLoading(false);
    if (e) { setError(e.message ?? "Could not load services."); return; }
    setRows(data ?? []);
  }, [showArchived]);

  useEffect(() => { load(); }, [load]);

  async function handleImportCatalog() {
    setImporting(true);
    const { data, error: e } = await supabase.rpc("seed_default_procedures");
    setImporting(false);
    if (e) { toast.error(e.message ?? "Could not import catalog."); return; }
    const inserted = Number(data?.inserted || 0);
    const updated = Number(data?.updated || 0);
    toast.success(`Built-in services checked: ${inserted} added, ${updated} repaired.`);
    load();
  }

  function replaceProcedureRows(updatedRows) {
    const returned = Array.isArray(updatedRows) ? updatedRows : [updatedRows].filter(Boolean);
    if (returned.length === 0) return;
    const byId = new Map(returned.map((row) => [row.id, row]));
    setRows((prev) => prev.map((row) => (byId.has(row.id) ? { ...row, ...byId.get(row.id) } : row)));
  }

  function beginInlinePriceEdit(row) {
    if (bulkEditing || row.archived) return;
    setPriceErrors((prev) => ({ ...prev, [row.id]: null }));
    setInlinePriceEditId(row.id);
    setInlinePriceDraft(centavosToPesoInput(row.default_fee_centavos));
  }

  function cancelInlinePriceEdit() {
    setInlinePriceEditId(null);
    setInlinePriceDraft("");
  }

  async function saveInlinePrice(row) {
    if (inlinePriceBusyId === row.id) return;
    const next = pricePesosToCentavos(inlinePriceDraft);
    if (next == null) {
      setPriceErrors((prev) => ({ ...prev, [row.id]: "Enter a price of 0 or more." }));
      return;
    }
    if (next === Number(row.default_fee_centavos || 0)) {
      cancelInlinePriceEdit();
      return;
    }

    setInlinePriceBusyId(row.id);
    setPriceErrors((prev) => ({ ...prev, [row.id]: null }));
    const { data, error: e } = await supabase.rpc("update_procedure_price", {
      p_procedure_id: row.id,
      p_fee_centavos: next,
    });
    setInlinePriceBusyId(null);
    if (e) {
      setPriceErrors((prev) => ({ ...prev, [row.id]: e.message ?? "Could not save price." }));
      return;
    }
    replaceProcedureRows(data);
    cancelInlinePriceEdit();
  }

  function beginBulkEditing() {
    setBulkEditing(true);
    setBulkError(null);
    setPriceErrors({});
    setInlinePriceEditId(null);
    setInlinePriceDraft("");
    setBulkDrafts(Object.fromEntries(rows.map((row) => [row.id, centavosToPesoInput(row.default_fee_centavos)])));
  }

  function cancelBulkEditing() {
    setBulkEditing(false);
    setBulkDrafts({});
    setBulkError(null);
    setAdjustPopoverCategory(null);
    setAdjustPercent("");
  }

  function updateBulkDraft(id, value) {
    setBulkDrafts((prev) => ({ ...prev, [id]: value }));
  }

  // Group rows by category (preserving the SQL order — alphabetical
  // within each category, archived sunk to the bottom).
  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? rows.filter((r) =>
          (r.name || "").toLowerCase().includes(q) ||
          (r.code || "").toLowerCase().includes(q) ||
          (r.short_code || "").toLowerCase().includes(q) ||
          (r.category || "").toLowerCase().includes(q) ||
          (multiToothKindLabel(r.multi_tooth_kind) || "").toLowerCase().includes(q) ||
          (r.multi_tooth_kind || "").toLowerCase().includes(q))
      : rows;
    const out = [];
    let lastKey = Symbol("none");
    for (const r of filtered) {
      const key = r.category || "Uncategorized";
      if (key !== lastKey) { out.push({ category: key, items: [] }); lastKey = key; }
      out[out.length - 1].items.push(r);
    }
    return out;
  }, [rows, search]);

  // Stable position-based hue map so the swatch shows the *effective*
  // color whether or not a clinic-custom override is set.
  const colorMap = useMemo(() => buildProcedureColorMap(rows), [rows]);

  const changedPrices = useMemo(() => {
    const changes = [];
    for (const row of rows) {
      if (!(row.id in bulkDrafts)) continue;
      const next = pricePesosToCentavos(bulkDrafts[row.id]);
      if (next == null || next === Number(row.default_fee_centavos || 0)) continue;
      changes.push({ id: row.id, fee_centavos: next });
    }
    return changes;
  }, [bulkDrafts, rows]);

  const knownCategories = APPROVED_PROCEDURE_CATEGORIES;

  async function saveBulkPrices() {
    if (changedPrices.length === 0) return;
    const invalid = rows.find((row) => {
      if (!(row.id in bulkDrafts)) return false;
      if (pricePesosToCentavos(bulkDrafts[row.id]) != null) return false;
      return String(bulkDrafts[row.id] ?? "").trim() !== centavosToPesoInput(row.default_fee_centavos);
    });
    if (invalid) {
      setBulkError(`Enter a price of 0 or more for ${invalid.name}.`);
      return;
    }

    setBulkSaving(true);
    setBulkError(null);
    const { data, error: e } = await supabase.rpc("bulk_update_procedure_prices", {
      p_updates: changedPrices,
    });
    setBulkSaving(false);
    if (e) {
      setBulkError(e.message ?? "Could not save prices.");
      return;
    }
    replaceProcedureRows(data);
    cancelBulkEditing();
    toast.success(`${changedPrices.length} prices updated.`);
  }

  function openAdjustPopover(category) {
    if (!bulkEditing) beginBulkEditing();
    setAdjustPopoverCategory(category);
    setAdjustDirection("+");
    setAdjustPercent("");
  }

  function applyCategoryPercent(items) {
    const percent = Number(adjustPercent);
    if (!Number.isFinite(percent) || percent <= 0) {
      setBulkError("Enter a percent greater than zero.");
      return;
    }
    const signed = adjustDirection === "-" ? -percent : percent;
    const multiplier = 1 + signed / 100;
    if (multiplier < 0) {
      setBulkError("The adjustment would make prices negative.");
      return;
    }

    setBulkError(null);
    setBulkDrafts((prev) => {
      const next = { ...prev };
      for (const row of items) {
        const current = pricePesosToCentavos(next[row.id]) ?? Number(row.default_fee_centavos || 0);
        const adjusted = Math.round((current * multiplier) / 100) * 100;
        next[row.id] = centavosToPesoInput(adjusted);
      }
      return next;
    });
    setAdjustPopoverCategory(null);
    setAdjustPercent("");
  }

  return (
    <div className="space-y-4" data-testid="settings-services" data-tutorial-id="settings-services-page">
      {/* Header bar */}
      <div className="bg-white rounded-2xl shadow-sm px-5 py-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Services</h1>
            <p className="text-xs text-slate-500">
              Your clinic price list and treatment setup. Built-in services stay available; add your own services anytime.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={bulkEditing ? cancelBulkEditing : beginBulkEditing}
              data-testid="services-edit-prices"
              data-tutorial-id="settings-services-edit-prices"
              className={[
                "px-3 py-2 text-sm font-semibold border rounded inline-flex items-center gap-1.5",
                bulkEditing
                  ? "border-rose-200 text-rose-700 hover:bg-rose-50"
                  : "border-slate-200 text-slate-700 hover:bg-slate-50",
              ].join(" ")}
            >
              {bulkEditing ? <X className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
              {bulkEditing ? "Cancel editing" : "Edit Prices"}
            </button>
            <button
              onClick={() => setPanel({ mode: "new" })}
              data-testid="services-new-button"
              data-tutorial-id="settings-services-add-service"
              className="px-4 py-2 text-sm font-semibold bg-teal-600 text-white rounded hover:bg-teal-700 inline-flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" /> Add service
            </button>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
            <input
              type="text" value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, code, or category…"
              data-testid="services-search"
              data-tutorial-id="settings-services-search"
              className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded focus:outline-none focus:border-teal-500"
            />
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox" checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              data-testid="services-show-archived"
              className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-300"
            />
            Show stopped services
          </label>
          <button
            type="button"
            onClick={handleImportCatalog}
            disabled={importing}
            data-testid="services-sync-catalog"
            data-tutorial-id="settings-services-built-in"
            title="Add missing built-in services and repair missing treatment setup. Your clinic edits are kept."
            className="px-3 py-2 text-sm font-semibold border border-slate-200 rounded text-slate-700 hover:bg-slate-50 disabled:opacity-40 inline-flex items-center gap-1.5"
          >
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
            Check built-in services
          </button>
        </div>
      </div>

      {error ? (
        <div role="alert" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="bg-white rounded-2xl shadow-sm p-8 text-sm text-slate-500 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : grouped.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm p-12 text-center" data-testid="services-empty-state">
          <p className="text-sm font-semibold text-slate-700">
            {search.trim() ? "No services match your search." : "No services yet"}
          </p>
          {!search.trim() ? (
            <>
              <p className="text-xs text-slate-500 mt-1 mb-4">
                Start with Dental OS built-in services, then add clinic-specific services anytime.
              </p>
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={handleImportCatalog}
                  disabled={importing}
                  data-testid="services-import-catalog"
                  className="px-4 py-2 text-sm font-semibold bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-40 inline-flex items-center gap-1.5"
                >
                  {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  Add built-in services
                </button>
                <button
                  onClick={() => setPanel({ mode: "new" })}
                  className="px-4 py-2 text-sm font-semibold border border-slate-200 rounded text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5"
                >
                  <Plus className="w-4 h-4" /> Add my first service
                </button>
              </div>
            </>
          ) : null}
        </div>
      ) : (
        <div
          className="space-y-4"
          data-tutorial-id={bulkEditing ? "settings-services-price-editor" : "settings-services-catalog"}
        >
        {grouped.map((g) => (
          <div key={g.category} className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3 bg-slate-50/50 group">
              <div className="flex items-center gap-2">
                <Tag className="w-3.5 h-3.5 text-slate-400" />
                <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{g.category}</p>
                <span className="text-[11px] text-slate-400">({g.items.length})</span>
              </div>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => openAdjustPopover(g.category)}
                  data-testid={`services-adjust-percent-${testIdSlug(g.category)}`}
                  className="px-2 py-1 text-[11px] font-semibold border border-slate-200 rounded text-slate-500 bg-white hover:text-teal-700 hover:border-teal-200 inline-flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition"
                >
                  <Percent className="w-3 h-3" />
                  Adjust %
                </button>
                {adjustPopoverCategory === g.category ? (
                  <div className="absolute right-0 top-8 z-20 w-64 rounded-lg border border-slate-200 bg-white shadow-lg p-3">
                    <div className="flex items-center gap-2">
                      <div className="inline-flex rounded border border-slate-200 overflow-hidden">
                        {["+", "-"].map((sign) => (
                          <button
                            key={sign}
                            type="button"
                            onClick={() => setAdjustDirection(sign)}
                            className={[
                              "w-8 h-8 text-sm font-semibold",
                              adjustDirection === sign ? "bg-teal-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50",
                            ].join(" ")}
                          >
                            {sign}
                          </button>
                        ))}
                      </div>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={adjustPercent}
                        onChange={(e) => setAdjustPercent(e.target.value)}
                        data-testid={`services-adjust-percent-input-${testIdSlug(g.category)}`}
                        className="w-20 px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-teal-500"
                      />
                      <span className="text-sm text-slate-500">%</span>
                      <button
                        type="button"
                        onClick={() => applyCategoryPercent(g.items)}
                        data-testid={`services-adjust-percent-apply-${testIdSlug(g.category)}`}
                        className="ml-auto px-3 py-2 text-xs font-semibold bg-teal-600 text-white rounded hover:bg-teal-700"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
            <ul className="divide-y divide-slate-100">
              {g.items.map((r) => (
                <li key={r.id}>
                  <div
                    className={[
                      "px-5 py-3 hover:bg-slate-50 flex items-center gap-3",
                      r.archived ? "opacity-50" : "",
                    ].join(" ")}
                  >
                    <button
                      type="button"
                      onClick={() => setColorPicker(r)}
                      title={r.color_hex ? `Custom color ${r.color_hex} — click to change` : "Default color — click to customise"}
                      aria-label={`Change color for ${r.name}`}
                      data-testid={`services-color-${r.id}`}
                      className="w-6 h-6 rounded-md border border-slate-200 shrink-0 hover:ring-2 hover:ring-slate-300 transition"
                      style={{ background: procedureColor(r, colorMap) }}
                    />
                    <button
                      type="button"
                      onClick={() => setPanel({ mode: "edit", row: r })}
                      data-testid={`services-row-${r.id}`}
                      className="flex-1 min-w-0 text-left flex items-center gap-3"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-slate-900 truncate">{r.name}</p>
                          {r.short_code ? (
                            <span className="text-[11px] text-slate-500 font-mono">({r.short_code})</span>
                          ) : null}
                          {r.is_platform_default ? (
                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-semibold inline-flex items-center gap-1">
                              <LockKeyhole className="w-2.5 h-2.5" /> Built-in
                            </span>
                          ) : null}
                          {r.archived ? (
                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-50 text-rose-600 font-semibold">
                              archived
                            </span>
                          ) : null}
                          {r.multi_visit ? (
                            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">multi-visit</span>
                          ) : null}
                          {r.multi_tooth_kind ? (
                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-100 font-semibold">
                              {multiToothKindLabel(r.multi_tooth_kind) || r.multi_tooth_kind}
                            </span>
                          ) : null}
                          {r.ortho_visit_type ? (
                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-100 font-semibold">
                              Ortho {orthoVisitTypeLabel(r.ortho_visit_type)}
                            </span>
                          ) : null}
                          {r.clears_calculus_deposits ? (
                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-700 border border-cyan-100 font-semibold">
                              clears calculus
                            </span>
                          ) : null}
                          {r.resolves_caries_findings ? (
                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-100 font-semibold">
                              resolves caries
                            </span>
                          ) : null}
                        </div>
                        {r.notes ? (
                          <p className="text-[11px] text-slate-500 truncate mt-0.5">{r.notes}</p>
                        ) : null}
                      </div>
                    </button>
                    <div className="text-right tabular-nums shrink-0 w-36">
                      <PriceCell
                        row={r}
                        bulkEditing={bulkEditing}
                        bulkValue={bulkDrafts[r.id] ?? centavosToPesoInput(r.default_fee_centavos)}
                        onBulkChange={(value) => updateBulkDraft(r.id, value)}
                        inlineEditing={inlinePriceEditId === r.id}
                        inlineValue={inlinePriceDraft}
                        inlineBusy={inlinePriceBusyId === r.id}
                        error={priceErrors[r.id]}
                        onBeginInline={() => beginInlinePriceEdit(r)}
                        onInlineChange={setInlinePriceDraft}
                        onInlineSave={() => saveInlinePrice(r)}
                        onInlineCancel={cancelInlinePriceEdit}
                      />
                      <p className="text-[11px] text-slate-500">
                        {r.suggested_duration_minutes
                          ? `${r.suggested_duration_minutes} min${r.unit_basis === "surface" ? " · per surface" : ""}${r.uses_chair_setup === false ? " · no setup" : ""}`
                          : "—"}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setPanel({ mode: "edit", row: r })}
                      aria-label={`Edit ${r.name}`}
                      className="p-1 rounded text-slate-300 hover:text-slate-600 hover:bg-slate-100 shrink-0"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
        </div>
      )}

      {/* Slide-in form panel */}
      <SidePanel
        open={!!panel}
        onClose={() => setPanel(null)}
        title={panel?.mode === "edit"
          ? (panel.row?.is_platform_default ? "Edit built-in service" : "Edit clinic service")
          : "Add new service"}
      >
        {panel ? (
          <ProcedureForm
            row={panel.mode === "edit" ? panel.row : null}
            rows={rows}
            knownCategories={knownCategories}
            onProceduresChanged={load}
            onSaved={() => { setPanel(null); load(); }}
            onCancel={() => setPanel(null)}
          />
        ) : null}
      </SidePanel>

      {colorPicker ? (
        <ColorPickerDialog
          procedure={colorPicker}
          colorMap={colorMap}
          onClose={() => setColorPicker(null)}
          onSaved={() => { setColorPicker(null); load(); }}
        />
      ) : null}

      {bulkEditing && changedPrices.length > 0 ? (
        <div
          data-testid="services-bulk-save-bar"
          className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur px-4 py-3 shadow-[0_-12px_30px_rgba(15,23,42,0.12)]"
        >
          <div className="mx-auto max-w-5xl flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-900">
                {changedPrices.length} {changedPrices.length === 1 ? "price" : "prices"} changed
              </p>
              {bulkError ? <p className="text-xs text-rose-600 mt-0.5">{bulkError}</p> : null}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={cancelBulkEditing}
                disabled={bulkSaving}
                data-testid="services-bulk-cancel"
                className="px-4 py-2 text-sm font-semibold border border-slate-200 rounded text-slate-700 hover:bg-slate-50 disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveBulkPrices}
                disabled={bulkSaving}
                data-testid="services-bulk-save"
                className="px-4 py-2 text-sm font-semibold bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-40 inline-flex items-center gap-1.5"
              >
                {bulkSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save all
              </button>
            </div>
          </div>
        </div>
      ) : bulkError ? (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-rose-200 bg-rose-50 px-4 py-3">
          <div className="mx-auto max-w-5xl flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-rose-700">{bulkError}</p>
            <button
              type="button"
              onClick={() => setBulkError(null)}
              className="p-1 rounded hover:bg-rose-100 text-rose-700"
              aria-label="Dismiss price error"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function PriceCell({
  row,
  bulkEditing,
  bulkValue,
  onBulkChange,
  inlineEditing,
  inlineValue,
  inlineBusy,
  error,
  onBeginInline,
  onInlineChange,
  onInlineSave,
  onInlineCancel,
}) {
  if (bulkEditing) {
    return (
      <input
        type="number"
        min="0"
        step="1"
        value={bulkValue}
        onChange={(e) => onBulkChange(e.target.value)}
        data-testid={`services-price-input-${row.id}`}
        aria-label={`Price for ${row.name}`}
        className="w-full px-2 py-1 text-sm font-semibold text-right border border-slate-200 rounded focus:outline-none focus:border-teal-500"
      />
    );
  }

  if (inlineEditing) {
    return (
      <div>
        <div className="flex items-center justify-end gap-1">
          <input
            type="number"
            min="0"
            step="1"
            value={inlineValue}
            onChange={(e) => onInlineChange(e.target.value)}
            onBlur={onInlineSave}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onInlineSave();
              }
              if (e.key === "Escape") {
                e.preventDefault();
                onInlineCancel();
              }
            }}
            disabled={inlineBusy}
            autoFocus
            data-testid={`services-price-input-${row.id}`}
            aria-label={`Price for ${row.name}`}
            className="w-28 px-2 py-1 text-sm font-semibold text-right border border-teal-300 rounded focus:outline-none focus:border-teal-600"
          />
          {inlineBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" /> : null}
        </div>
        {error ? <p className="text-[10px] text-rose-600 mt-1">{error}</p> : null}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onBeginInline}
      data-testid={`services-price-display-${row.id}`}
      className="text-sm font-semibold text-slate-900 hover:text-teal-700 hover:underline decoration-teal-300 underline-offset-2"
    >
      {formatMoney(row.default_fee_centavos, row.currency)}
    </button>
  );
}

// ---------------------------------------------------------------------
// ColorPickerDialog — pick a custom procedure color, or reset to default
// ---------------------------------------------------------------------
function ColorPickerDialog({ procedure, colorMap, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  // Default-ramp hue this procedure would have without an override —
  // shown as the "(default)" preview when reset.
  const defaultHue = useMemo(
    () => procedureColor({ ...procedure, color_hex: null }, colorMap),
    [procedure, colorMap]
  );
  const palette = CATEGORY_HUES[procedure.category] || null;
  const current = procedure.color_hex || defaultHue;
  const [picked, setPicked] = useState(current);

  async function applyColor(hex) {
    setBusy(true);
    const { error } = await supabase.rpc("update_procedure_color", {
      p_procedure_id: procedure.id,
      p_color_hex:    hex,
    });
    setBusy(false);
    if (error) {
      toast.error(error.message ?? "Could not update color.");
      return;
    }
    toast.success(hex ? "Color updated." : "Reset to default.");
    onSaved?.();
  }

  return (
    <div
      role="dialog" aria-modal="true" aria-labelledby="color-picker-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
      onClick={() => !busy && onClose()}
      data-testid="color-picker-dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden"
      >
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="min-w-0">
            <p id="color-picker-title" className="text-sm font-semibold text-slate-900 truncate">
              Color for {procedure.name}
            </p>
            <p className="text-[11px] text-slate-500">{procedure.category || "Uncategorized"}</p>
          </div>
          <button
            type="button" onClick={onClose} disabled={busy}
            data-testid="color-picker-close"
            className="p-1 rounded hover:bg-slate-100 text-slate-500"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Quick palette — category's 5-hue family, dark → light */}
          {palette ? (
            <div>
              <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">
                {procedure.category} palette
              </p>
              <div className="flex items-center gap-2">
                {palette.map((hue) => (
                  <button
                    key={hue}
                    type="button"
                    onClick={() => { setPicked(hue); applyColor(hue); }}
                    disabled={busy}
                    title={hue}
                    aria-label={`Set color to ${hue}`}
                    data-testid={`color-picker-palette-${hue}`}
                    className={[
                      "w-9 h-9 rounded-md border transition",
                      picked?.toLowerCase() === hue.toLowerCase()
                        ? "border-slate-900 ring-2 ring-slate-900"
                        : "border-slate-200 hover:ring-2 hover:ring-slate-300",
                    ].join(" ")}
                    style={{ background: hue }}
                  />
                ))}
              </div>
            </div>
          ) : (
            <p className="text-[11px] text-slate-500">
              No palette family for "{procedure.category || "Uncategorized"}". Use the custom picker below.
            </p>
          )}

          {/* Custom hex picker */}
          <div>
            <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">
              Custom
            </p>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={picked || "#0d9488"}
                onChange={(e) => setPicked(e.target.value)}
                disabled={busy}
                data-testid="color-picker-custom"
                className="w-10 h-10 rounded border border-slate-200 cursor-pointer p-0"
              />
              <input
                type="text" value={picked || ""}
                onChange={(e) => setPicked(e.target.value)}
                placeholder="#RRGGBB"
                disabled={busy}
                className="flex-1 px-2.5 py-1.5 text-sm border border-slate-200 rounded font-mono focus:outline-none focus:border-teal-500"
              />
              <button
                type="button"
                onClick={() => applyColor(picked)}
                disabled={busy || !picked || !/^#[0-9a-fA-F]{6}$/.test(picked)}
                data-testid="color-picker-apply"
                className="px-3 py-2 text-xs font-semibold bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-40"
              >
                Apply
              </button>
            </div>
          </div>

          {/* Reset to default */}
          <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <span
                className="w-5 h-5 rounded border border-slate-200 shrink-0"
                style={{ background: defaultHue }}
              />
              <p className="text-[11px] text-slate-500 truncate">
                Default: <span className="font-mono">{defaultHue}</span>
                {procedure.color_hex ? "" : " (in use)"}
              </p>
            </div>
            <button
              type="button"
              onClick={() => { setPicked(defaultHue); applyColor(null); }}
              disabled={busy || !procedure.color_hex}
              data-testid="color-picker-reset"
              className="px-3 py-2 text-xs border border-slate-200 rounded text-slate-600 hover:bg-slate-50 disabled:opacity-40 inline-flex items-center gap-1.5"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
              Reset to default
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// ProcedureForm — used for both create and edit
// ---------------------------------------------------------------------
function ProcedureForm({ row, rows = [], knownCategories, onProceduresChanged, onSaved, onCancel }) {
  const isEdit = !!row;
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState(null);

  // Form state — pesos for fee (UX), centavos at submit time.
  const [name,      setName]      = useState(row?.name || "");
  const [shortCode, setShortCode] = useState(row?.short_code || "");
  const [code,      setCode]      = useState(row?.code || "");
  const [category,  setCategory]  = useState(row?.category || "");
  const [categoryError, setCategoryError] = useState(null);
  const [feePesos,  setFeePesos]  = useState(row?.default_fee_centavos != null ? (row.default_fee_centavos / 100).toString() : "");
  const [duration,  setDuration]  = useState(row?.suggested_duration_minutes ?? "");
  const [unitBasis, setUnitBasis] = useState(row?.unit_basis || "fixed");
  const [usesChairSetup, setUsesChairSetup] = useState(row?.uses_chair_setup ?? true);
  const [multiVisit, setMultiVisit] = useState(!!row?.multi_visit);
  // How many days the booking modal's "Suggested" hint defaults to.
  // Drives the smart-default date for both Step 3 in Checkout and the
  // Tx Plan Center drawer's "Book First/Next Visit" button. NULL means
  // fall back to the legacy category map in lib/procedureFlags.
  const [bookingWindowDays, setBookingWindowDays] = useState(
    row?.default_booking_window_days != null ? String(row.default_booking_window_days) : "",
  );
  // tooth_specific / surface_specific gate how the chairside chart
  // paints this procedure. Defaults match the existing
  // isWholeToothProcedure regex so legacy rows behave identically.
  const [toothSpecific,   setToothSpecific]   = useState(row?.tooth_specific   ?? true);
  const [surfaceSpecific, setSurfaceSpecific] = useState(row?.surface_specific ?? false);
  // Bundle policy — % of catalog rate to charge for each ADDITIONAL
  // surface in a multi-surface bundle (Composite MOD = 1 base + 2
  // additional). 50 = the legacy "half off each extra" rule. 100 =
  // honest full-price-per-surface (matches honest-pricing default).
  // Shown as the suggested preset on the chairside Discount popover's
  // Multi-surface chip; doctor still applies it deliberately.
  const [bundlePct, setBundlePct] = useState(
    row?.additional_surface_pct != null ? String(row.additional_surface_pct) : "100",
  );
  // Extraction toggle — when true, completing this procedure auto-marks
  // the tooth Missing on the chart (locked, admin-override only).
  const [isExtraction, setIsExtraction] = useState(!!row?.is_extraction);
  const [clearsCalculus, setClearsCalculus] = useState(!!row?.clears_calculus_deposits);
  const [resolvesCaries, setResolvesCaries] = useState(!!row?.resolves_caries_findings);
  const [toothCount, setToothCount] = useState(row?.tooth_count || "single");
  const [multiToothKind, setMultiToothKind] = useState(row?.multi_tooth_kind || "");
  const [toothRoleRequired, setToothRoleRequired] = useState(!!row?.tooth_role_required);
  const [requiresLabWork, setRequiresLabWork] = useState(!!row?.requires_lab_work);
  // Post-op follow-up: when enabled, completing this procedure auto-creates
  // an Open treatment_need on the patient using the picked follow-up
  // procedure and after-days, which then surfaces in Checkout Step 3.
  const [postOpFollowupEnabled, setPostOpFollowupEnabled] = useState(!!row?.post_op_followup_enabled);
  const [postOpFollowupAfterDays, setPostOpFollowupAfterDays] = useState(
    row?.post_op_followup_after_days != null ? String(row.post_op_followup_after_days) : "7",
  );
  const [postOpFollowupProcedureId, setPostOpFollowupProcedureId] = useState(row?.post_op_followup_procedure_id || "");
  const [createdFollowupProcedures, setCreatedFollowupProcedures] = useState([]);
  const [creatingFollowupProcedure, setCreatingFollowupProcedure] = useState(false);
  const [orthoVisitType, setOrthoVisitType] = useState(row?.ortho_visit_type || "");
  const [visitCount, setVisitCount] = useState(
    row?.visit_count != null ? String(row.visit_count) : "1",
  );
  const [visitSteps, setVisitSteps] = useState(() =>
    normalizeVisitSteps(row?.default_visit_template),
  );
  const [notes,     setNotes]     = useState(row?.notes || "");

  const bundlePctNum = bundlePct === "" ? null : Number(bundlePct);
  const bundlePctValid = bundlePctNum == null || (bundlePctNum >= 0 && bundlePctNum <= 100);
  const visitCountNum = Number(visitCount);
  const visitCountValid = Number.isInteger(visitCountNum) && visitCountNum > 0;
  const postOpFollowupDaysNum = Number(postOpFollowupAfterDays);
  const postOpFollowupValid = !postOpFollowupEnabled
    || (
      Number.isInteger(postOpFollowupDaysNum)
      && postOpFollowupDaysNum > 0
      && postOpFollowupDaysNum <= 365
      && !!postOpFollowupProcedureId
    );
  const visitStepsValid = visitSteps.every((step) => (
    String(step.label || "").trim().length > 0
    && optionalWholeNumberIsValid(step.chair_min, 1, 480)
    && optionalWholeNumberIsValid(step.lab_gap_days, 0, 365)
  ));
  const visitScheduleCountValid = visitSteps.length === 0
    || visitSteps.length === visitCountNum;
  const canSave = name.trim().length > 0
    && (feePesos !== "" || !!orthoVisitType)
    && (feePesos === "" || Number(feePesos) >= 0)
    && bundlePctValid
    && visitCountValid
    && postOpFollowupValid
    && visitStepsValid
    && visitScheduleCountValid;
  const orthoVisitLinkOwners = useMemo(() => {
    const owners = new Map();
    for (const procedure of rows) {
      if (!procedure?.ortho_visit_type || procedure.archived || procedure.id === row?.id) continue;
      const list = owners.get(procedure.ortho_visit_type) || [];
      list.push(procedure.name);
      owners.set(procedure.ortho_visit_type, list);
    }
    return owners;
  }, [rows, row?.id]);
  const chartPlacement = !toothSpecific
    ? "whole_mouth"
    : surfaceSpecific
      ? "surface"
      : "whole_tooth";
  const followupProcedureOptions = useMemo(() => {
    const byId = new Map();
    for (const procedure of [...rows, ...createdFollowupProcedures]) {
      if (!procedure?.id || procedure.archived || procedure.id === row?.id) continue;
      byId.set(procedure.id, procedure);
    }
    return Array.from(byId.values()).sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""))
    );
  }, [rows, createdFollowupProcedures, row?.id]);
  const hasPostOpProcedure = followupProcedureOptions.some(isPostOpProcedure);

  function handleChartPlacementChange(nextPlacement) {
    if (nextPlacement === "whole_mouth") {
      setToothSpecific(false);
      setSurfaceSpecific(false);
      if (!multiToothKind) setToothCount("none");
      return;
    }
    if (nextPlacement === "surface") {
      setToothSpecific(true);
      setSurfaceSpecific(true);
      if (toothCount === "none") setToothCount("single");
      return;
    }
    setToothSpecific(true);
    setSurfaceSpecific(false);
    if (toothCount === "none" && !multiToothKind) setToothCount("single");
  }

  function handleMultiToothKindChange(nextKind) {
    setMultiToothKind(nextKind);
    if (nextKind === "bridge") {
      setToothCount("multi");
      setMultiVisit(true);
      setToothRoleRequired(true);
      setRequiresLabWork(true);
      if (!visitCountValid || visitCountNum < 3) setVisitCount("3");
    } else if (nextKind === "partial_denture") {
      setToothCount("multi");
      setMultiVisit(true);
      setToothRoleRequired(false);
      setRequiresLabWork(true);
      if (!visitCountValid || visitCountNum < 4) setVisitCount("4");
    } else if (nextKind === "full_denture") {
      setToothCount("none");
      setMultiVisit(true);
      setToothRoleRequired(false);
      setRequiresLabWork(true);
      if (!visitCountValid || visitCountNum < 4) setVisitCount("4");
    } else if (nextKind === "crown_unit") {
      setToothCount("single");
      setMultiVisit(true);
      setToothRoleRequired(false);
      setRequiresLabWork(true);
    }
  }

  function handleMultiVisitChange(nextMultiVisit) {
    setMultiVisit(nextMultiVisit);
    if (nextMultiVisit && (!visitCountValid || visitCountNum < 2)) {
      setVisitCount("2");
    }
    if (!nextMultiVisit) {
      setVisitCount("1");
      setVisitSteps([]);
    }
  }

  function updateVisitStep(index, field, value) {
    setVisitSteps((current) => current.map((step, stepIndex) => (
      stepIndex === index ? { ...step, [field]: value } : step
    )));
  }

  function addVisitStep() {
    setMultiVisit(true);
    const nextLength = visitSteps.length + 1;
    setVisitSteps((current) => [
      ...current,
      {
        label: `Visit ${current.length + 1}`,
        chair_min: duration === "" ? "" : String(duration),
        lab_gap_days: "",
      },
    ]);
    if (!visitCountValid || visitCountNum < nextLength) {
      setVisitCount(String(Math.max(2, nextLength)));
    }
  }

  function removeVisitStep(index) {
    setVisitSteps((current) => current.filter((_, stepIndex) => stepIndex !== index));
  }

  async function handlePostOpFollowupProcedureChange(event) {
    const nextValue = event.target.value;
    if (nextValue !== CREATE_POST_OP_PROCEDURE_VALUE) {
      setPostOpFollowupProcedureId(nextValue);
      return;
    }

    setCreatingFollowupProcedure(true);
    setError(null);
    const { data, error: e } = await supabase.rpc("create_procedure", {
      p_input: DEFAULT_POST_OP_PROCEDURE_INPUT,
    });
    setCreatingFollowupProcedure(false);

    if (e) {
      setError(e.message ?? "Could not create Post-Op procedure.");
      return;
    }

    const created = Array.isArray(data) ? data[0] : data;
    if (!created?.id) {
      setError("Post-Op procedure was created but the database did not return its ID.");
      return;
    }

    setCreatedFollowupProcedures((prev) => (
      prev.some((procedure) => procedure.id === created.id) ? prev : [...prev, created]
    ));
    setPostOpFollowupProcedureId(created.id);
    toast.success("Post-Op procedure added.");
    onProceduresChanged?.();
  }

  async function handleSave() {
    const cleanCategory = category.trim();
    if (!isApprovedProcedureCategory(cleanCategory)) {
      setCategoryError("Pick a category from the approved list.");
      return;
    }
    if (!visitStepsValid) {
      setError("Each visit needs a name. Time and waiting days must be whole numbers.");
      return;
    }
    if (!visitCountValid) {
      setError("Visit count must be a positive whole number.");
      return;
    }
    if (!visitScheduleCountValid) {
      setError("Planned appointments must match the number of named visits.");
      return;
    }
    setBusy(true); setError(null);
    const feeCentavos = feePesos === "" ? null : Math.round(Number(feePesos) * 100);
    const payload = {
      name:                       name.trim(),
      short_code:                 shortCode.trim() || null,
      code:                       code.trim() || null,
      category:                   cleanCategory,
      default_fee_centavos:       feeCentavos,
      suggested_duration_minutes: duration === "" ? null : Number(duration),
      unit_basis:                 unitBasis,
      uses_chair_setup:           usesChairSetup,
      multi_visit:                multiVisit,
      default_booking_window_days: bookingWindowDays === "" ? null : Number(bookingWindowDays),
      tooth_specific:             toothSpecific,
      surface_specific:           surfaceSpecific,
      additional_surface_pct:     bundlePctNum,
      is_extraction:              isExtraction,
      clears_calculus_deposits:   clearsCalculus,
      resolves_caries_findings:   resolvesCaries,
      tooth_count:                toothCount,
      multi_tooth_kind:           multiToothKind || null,
      tooth_role_required:        toothRoleRequired,
      requires_lab_work:          requiresLabWork,
      post_op_followup_enabled:   postOpFollowupEnabled,
      post_op_followup_after_days: postOpFollowupEnabled
        ? (postOpFollowupAfterDays === "" ? null : Number(postOpFollowupAfterDays))
        : null,
      post_op_followup_procedure_id: postOpFollowupEnabled ? (postOpFollowupProcedureId || null) : null,
      ortho_visit_type:           orthoVisitType || null,
      visit_count:                visitCountNum,
      default_visit_template:     serializeVisitSteps(visitSteps),
      notes:                      notes.trim() || null,
    };
    const rpc = isEdit ? "update_procedure" : "create_procedure";
    if (isEdit) payload.id = row.id;
    const { error: e } = await supabase.rpc(rpc, { p_input: payload });
    setBusy(false);
    if (e) { setError(e.message ?? "Could not save."); return; }
    toast.success(isEdit ? "Procedure updated." : "Procedure added.");
    onSaved?.();
  }

  async function handleArchiveToggle() {
    if (!isEdit) return;
    if (row.is_platform_default) {
      setError("Built-in services stay available. Edit this service to match how your clinic works.");
      return;
    }
    setBusy(true); setError(null);
    const rpc = row.archived ? "unarchive_procedure" : "archive_procedure";
    const { error: e } = await supabase.rpc(rpc, { p_id: row.id });
    setBusy(false);
    if (e) { setError(e.message ?? "Could not change archive state."); return; }
    toast.success(row.archived ? "Procedure restored." : "Procedure archived.");
    onSaved?.();
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {row?.is_platform_default ? (
          <div
            data-testid="proc-built-in-notice"
            className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm text-slate-600"
          >
            <LockKeyhole className="w-4 h-4 mt-0.5 text-slate-500 shrink-0" />
            <p className="leading-relaxed">
              <span className="font-semibold text-slate-800">This service is always available.</span>{" "}
              Dental OS keeps it because charting and treatment plans rely on it. You can change it to match how your clinic works, but it cannot be removed.
            </p>
          </div>
        ) : null}

        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Everyday details</p>
          <p className="mt-1 text-sm text-slate-500">What staff need when choosing and booking this service.</p>
        </div>

        <Field label="Service name" required>
          <input
            type="text" value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="proc-name"
            autoFocus
            className={DRAWER_CONTROL_CLASS}
          />
        </Field>

        <Field label="Category" required>
          <select
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setCategoryError(null);
            }}
            data-testid="proc-category"
            className={`${DRAWER_CONTROL_CLASS} ${
              categoryError ? "border-rose-300 focus:border-rose-500" : ""
            }`}
          >
            <option value="">Pick category...</option>
            {knownCategories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {categoryError ? (
            <p data-testid="proc-category-error" className="text-[11px] text-rose-600 mt-1">
              {categoryError}
            </p>
          ) : null}
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Clinic price" required>
            <div className="relative">
              <span className="absolute left-3 top-3 text-sm text-slate-500">{CURRENCY_SYMBOL}</span>
              <input
                type="number" min="0" step="0.01" value={feePesos}
                onChange={(e) => setFeePesos(e.target.value)}
                placeholder={orthoVisitType ? "Depends on case" : "0.00"}
                data-testid="proc-fee"
                className={`${DRAWER_CONTROL_CLASS} pl-7`}
              />
            </div>
            {orthoVisitType ? (
              <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                Leave blank for active Ortho procedures billed from the case contract.
              </p>
            ) : null}
          </Field>
          <Field label="Usual appointment time">
            <div className="flex items-center gap-2">
              <input
                type="number" min="1" max="480" value={duration}
                onChange={(e) => setDuration(e.target.value)}
                list="known-durations"
                placeholder="—"
                data-testid="proc-duration"
                className={`${DRAWER_CONTROL_CLASS} w-24`}
              />
              <datalist id="known-durations">
                {DEFAULT_DURATIONS.map((d) => <option key={d} value={d} />)}
              </datalist>
              <span className="text-sm text-slate-500">
                min{unitBasis === "surface" ? " per surface" : ""}
              </span>
            </div>
            <p className="mt-1 text-xs leading-snug text-slate-500">
              The time usually blocked on the calendar. Extra chair setup can be added below.
            </p>
          </Field>
        </div>

        <DisclosureSection
          title="Appointment timing rules"
          description="Open this only if time changes by surface or your clinic adds a setup block."
          defaultOpen={unitBasis === "surface" || usesChairSetup === false}
          testId="proc-section-timing"
        >

          <div className="flex items-start gap-3">
            <span className="w-20 shrink-0 pt-1 text-sm text-slate-700">Time applies</span>
            <div className="flex flex-col gap-1.5">
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="radio" name="unit-basis" value="fixed"
                  checked={unitBasis === "fixed"}
                  onChange={() => setUnitBasis("fixed")}
                  data-testid="proc-unit-basis-fixed"
                  className="h-4 w-4 text-teal-600 focus:ring-teal-300"
                />
                <span><span className="font-medium">Once for the service</span><span className="block text-xs text-slate-500">Use for exams, cleaning, extraction, and most services.</span></span>
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="radio" name="unit-basis" value="surface"
                  checked={unitBasis === "surface"}
                  onChange={() => setUnitBasis("surface")}
                  data-testid="proc-unit-basis-surface"
                  className="h-4 w-4 text-teal-600 focus:ring-teal-300"
                />
                <span><span className="font-medium">For every tooth surface</span><span className="block text-xs text-slate-500">Use when each added surface needs more chair time.</span></span>
              </label>
            </div>
          </div>

          <label className="inline-flex items-start gap-2 text-sm text-slate-700 pt-1">
            <input
              type="checkbox" checked={usesChairSetup}
              onChange={(e) => setUsesChairSetup(e.target.checked)}
              data-testid="proc-uses-chair-setup"
              className={DRAWER_CHECKBOX_CLASS}
            />
            <span>
              <span className="font-medium">Add the clinic's chair setup time</span>
              <span className="block text-xs text-slate-500">
                Adds your usual preparation and cleanup block once per appointment.
              </span>
            </span>
          </label>
        </DisclosureSection>

        <div className="space-y-3 border-t border-slate-100 pt-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Chart use</p>
            <p className="mt-1 text-sm text-slate-500">Choose what staff select before adding this service.</p>
          </div>
          <Field label="Where is this service used?">
            <select
              value={chartPlacement}
              onChange={(e) => handleChartPlacementChange(e.target.value)}
              data-testid="proc-chart-placement"
              className={DRAWER_CONTROL_CLASS}
            >
              <option value="whole_mouth">No specific tooth, such as exams, cleaning, or x-rays</option>
              <option value="whole_tooth">One whole tooth</option>
              <option value="surface">Tooth surfaces</option>
            </select>
            <p className="mt-1 text-xs leading-snug text-slate-500">
              Examples: cleaning uses no specific tooth, a crown uses one tooth, and a filling uses tooth surfaces.
            </p>
          </Field>

          <DisclosureSection
            title="What changes on the chart when treatment is done?"
            description="These are usually already set correctly for built-in services."
            defaultOpen={isExtraction || clearsCalculus || resolvesCaries}
            testId="proc-section-chart-effects"
          >

          <label className="inline-flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox" checked={isExtraction}
              onChange={(e) => setIsExtraction(e.target.checked)}
              disabled={!toothSpecific}
              data-testid="proc-is-extraction"
              className={`${DRAWER_CHECKBOX_CLASS} text-rose-600 disabled:opacity-40`}
            />
            <span className={!toothSpecific ? "opacity-40" : ""}>
              <span className="font-medium">Mark the tooth missing when this work is completed</span>
              <span className="block text-xs text-slate-500">
                Use for extraction. Only a clinic admin can undo the missing-tooth mark.
              </span>
            </span>
          </label>

          <label className="inline-flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox" checked={clearsCalculus}
              onChange={(e) => setClearsCalculus(e.target.checked)}
              data-testid="proc-clears-calculus"
              className={`${DRAWER_CHECKBOX_CLASS} text-cyan-600`}
            />
            <span>
              <span className="font-medium">Clear calculus marks when this work is completed</span>
              <span className="block text-xs text-slate-500">
                Use for cleaning, scaling and root planing, and periodontal maintenance.
              </span>
            </span>
          </label>

          <label className="inline-flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox" checked={resolvesCaries}
              onChange={(e) => setResolvesCaries(e.target.checked)}
              disabled={!toothSpecific}
              data-testid="proc-resolves-caries"
              className={`${DRAWER_CHECKBOX_CLASS} text-emerald-600 disabled:opacity-40`}
            />
            <span className={!toothSpecific ? "opacity-40" : ""}>
              <span className="font-medium">Show matching caries as treated</span>
              <span className="block text-xs text-slate-500">
                Use for fillings and restorations. Older chart records stay unchanged.
              </span>
            </span>
          </label>

          </DisclosureSection>

          {surfaceSpecific ? (
            <DisclosureSection
              title="Price for extra surfaces"
              description="Choose what each added surface costs after the first one."
              defaultOpen={bundlePctNum !== 100}
              testId="proc-section-surface-price"
            >
              <div className="flex items-start gap-2">
                <Field label="Each extra surface costs">
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min="0" max="100" step="1"
                      value={bundlePct}
                      onChange={(e) => setBundlePct(e.target.value)}
                      data-testid="proc-bundle-pct"
                      className={`${DRAWER_CONTROL_CLASS} w-24 ${
                        bundlePctValid ? "" : "border-rose-300 focus:border-rose-500"
                      }`}
                    />
                    <span className="text-sm text-slate-500">%</span>
                  </div>
                </Field>
              </div>
              <p className="text-xs leading-snug text-slate-500">
                The first surface uses the full clinic price. Each extra surface uses {bundlePctNum ?? 100}% of that price.
                {bundlePctNum === 100
                  ? " At 100%, every surface uses the full price."
                  : bundlePctNum === 0
                    ? " At 0%, only the first surface is charged."
                    : " Extra surfaces receive a lower price."}
              </p>
            </DisclosureSection>
          ) : null}
        </div>

        <DisclosureSection
          title="Follow-up after treatment"
          description="Use this when staff should be reminded to book a post-treatment check."
          defaultOpen={postOpFollowupEnabled}
          testId="proc-section-followup"
        >
          <label className="inline-flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={postOpFollowupEnabled}
              onChange={(e) => setPostOpFollowupEnabled(e.target.checked)}
              data-testid="proc-post-op-followup-enabled"
              className={DRAWER_CHECKBOX_CLASS}
            />
            <span>
              <span className="font-medium">Suggest a follow-up at checkout</span>
              <span className="block text-xs text-slate-500">
                Good for surgical extraction, implant surgery, or any service that needs a post-op check.
              </span>
            </span>
          </label>
          {postOpFollowupEnabled ? (
            <div className="grid grid-cols-[120px_1fr] gap-2 pl-6">
              <Field label="Days after">
                <input
                  type="number"
                  min="1"
                  max="365"
                  step="1"
                  value={postOpFollowupAfterDays}
                  onChange={(e) => setPostOpFollowupAfterDays(e.target.value)}
                  data-testid="proc-post-op-followup-days"
                  className={DRAWER_CONTROL_CLASS}
                />
              </Field>
              <Field label="Follow-up procedure">
                <select
                  value={postOpFollowupProcedureId}
                  onChange={handlePostOpFollowupProcedureChange}
                  disabled={creatingFollowupProcedure}
                  data-testid="proc-post-op-followup-procedure"
                  className={DRAWER_CONTROL_CLASS}
                >
                  <option value="">{creatingFollowupProcedure ? "Creating Post-Op..." : "Pick a follow-up procedure..."}</option>
                  {followupProcedureOptions.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                  {!hasPostOpProcedure ? (
                    <option value={CREATE_POST_OP_PROCEDURE_VALUE}>+ Create Post-Op procedure (₱0)</option>
                  ) : null}
                </select>
              </Field>
              {!postOpFollowupProcedureId ? (
                <p className="col-span-2 text-[11px] text-amber-700 pl-1">
                  Pick which procedure to use for the follow-up, usually "Post-Op."
                </p>
              ) : null}
            </div>
          ) : null}
        </DisclosureSection>

        <DisclosureSection
          title="Treatment plan setup"
          description="Open for bridges, dentures, crowns, veneers, or treatment done over several visits."
          defaultOpen={multiVisit || !!multiToothKind || requiresLabWork || toothRoleRequired || visitSteps.length > 0}
          testId="proc-section-treatment-plan"
        >
          <Field label="Special treatment type">
            <select
              value={multiToothKind}
              onChange={(e) => handleMultiToothKindChange(e.target.value)}
              data-testid="proc-multi-tooth-kind"
              className={DRAWER_CONTROL_CLASS}
            >
              <option value="">Regular service</option>
              <option value="bridge">Bridge</option>
              <option value="partial_denture">Partial Denture</option>
              <option value="full_denture">Full Denture</option>
              <option value="crown_unit">Crown or veneer units</option>
            </select>
            <p className="mt-1 text-xs leading-snug text-slate-500">
              This tells Dental OS how staff choose teeth when adding the service to a treatment plan.
            </p>
            {multiToothKind === "crown_unit" ? (
              <p className="mt-2 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs leading-snug text-amber-800">
                Staff can select several teeth as one case with one total fee and one shared visit schedule.
              </p>
            ) : null}
          </Field>

          <div>
            <Field label="Tooth selection">
              <select
                value={toothCount}
                onChange={(e) => setToothCount(e.target.value)}
                data-testid="proc-tooth-count"
                className={DRAWER_CONTROL_CLASS}
              >
                <option value="single">One tooth</option>
                <option value="multi">Several teeth or a tooth span</option>
                <option value="none">Whole arch or no specific tooth</option>
              </select>
            </Field>
          </div>

          <label className="inline-flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={requiresLabWork}
              onChange={(e) => setRequiresLabWork(e.target.checked)}
              data-testid="proc-requires-lab-work"
              className={DRAWER_CHECKBOX_CLASS}
            />
            <span>
              <span className="font-medium">Send work to a dental lab</span>
              <span className="block text-xs text-slate-500">
                Use for bridges, crowns, dentures, and other lab-made work.
              </span>
            </span>
          </label>

          <label className="inline-flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={toothRoleRequired}
              onChange={(e) => setToothRoleRequired(e.target.checked)}
              disabled={toothCount !== "multi"}
              data-testid="proc-tooth-role-required"
              className={`${DRAWER_CHECKBOX_CLASS} disabled:opacity-40`}
            />
            <span className={toothCount !== "multi" ? "opacity-40" : ""}>
              <span className="font-medium">Ask which teeth are abutments and pontics</span>
              <span className="block text-xs text-slate-500">
                Keep this on for bridges so every tooth has the correct role.
              </span>
            </span>
          </label>

          <div className="border-t border-slate-200 pt-3">
            <label className="inline-flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={multiVisit}
                onChange={(e) => handleMultiVisitChange(e.target.checked)}
                data-testid="proc-multi-visit"
                className={DRAWER_CHECKBOX_CLASS}
              />
              <span>
                <span className="font-medium">This service needs several appointments</span>
                <span className="block text-xs text-slate-500">
                  Use for root canal, dentures, bridges, and other work completed in stages.
                </span>
              </span>
            </label>

            {multiVisit ? (
              <div className="mt-4 space-y-3 pl-7">
                <Field label="Planned appointments">
                  <input
                    type="number"
                    min="2"
                    step="1"
                    value={visitCount}
                    onChange={(e) => setVisitCount(e.target.value)}
                    data-testid="proc-visit-count"
                    className={`${DRAWER_CONTROL_CLASS} w-28 ${
                      visitCountValid ? "" : "border-rose-300 focus:border-rose-500"
                    }`}
                  />
                </Field>

                <div data-testid="proc-default-visit-template" className="space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold text-slate-700">Visit names and timing</p>
                      <p className="mt-0.5 text-xs text-slate-500">Optional. Name the steps when staff need a clear visit-by-visit guide.</p>
                    </div>
                    <button
                      type="button"
                      onClick={addVisitStep}
                      className="inline-flex min-h-11 shrink-0 items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      data-testid="proc-add-visit-step"
                    >
                      <Plus className="h-3.5 w-3.5" /> Add visit
                    </button>
                  </div>

                  {!visitScheduleCountValid ? (
                    <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs leading-snug text-rose-700" data-testid="proc-visit-count-mismatch">
                      This service plans {visitCountNum} appointments but has {visitSteps.length} named visits. Change the appointment count or add/remove names so they match.
                    </p>
                  ) : null}

                  {visitSteps.length === 0 ? (
                    <p className="rounded-lg border border-dashed border-slate-200 px-3 py-3 text-xs leading-snug text-slate-500">
                      Staff will see simple labels like Visit 1 and Visit 2. Add visit names only when the stages need to be specific.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {visitSteps.map((step, index) => (
                        <div key={index} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`proc-visit-step-${index}`}>
                          <div className="mb-2 flex items-center justify-between gap-2">
                            <p className="text-xs font-semibold text-slate-700">Visit {index + 1}</p>
                            <button
                              type="button"
                              onClick={() => removeVisitStep(index)}
                              aria-label={`Remove visit ${index + 1}`}
                              className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                          <Field label="What happens at this visit?">
                            <input
                              type="text"
                              value={step.label}
                              onChange={(e) => updateVisitStep(index, "label", e.target.value)}
                              placeholder={`Visit ${index + 1}`}
                              data-testid={`proc-visit-step-${index}-label`}
                              className={`${DRAWER_CONTROL_CLASS} ${String(step.label || "").trim() ? "" : "border-rose-300"}`}
                            />
                          </Field>
                          <div className="mt-3 grid grid-cols-2 gap-3">
                            <Field label="Chair time (minutes)">
                              <input
                                type="number"
                                min="1"
                                max="480"
                                step="1"
                                value={step.chair_min}
                                onChange={(e) => updateVisitStep(index, "chair_min", e.target.value)}
                                data-testid={`proc-visit-step-${index}-chair-min`}
                                className={DRAWER_CONTROL_CLASS}
                              />
                            </Field>
                            <Field label="Wait after (days)">
                              <input
                                type="number"
                                min="0"
                                max="365"
                                step="1"
                                value={step.lab_gap_days}
                                onChange={(e) => updateVisitStep(index, "lab_gap_days", e.target.value)}
                                data-testid={`proc-visit-step-${index}-gap-days`}
                                className={DRAWER_CONTROL_CLASS}
                              />
                            </Field>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </DisclosureSection>

        <DisclosureSection
          title="Booking suggestion"
          description={bookingWindowDays !== ""
            ? `Currently suggests ${bookingWindowDays} days ahead.`
            : "Uses the usual timing for this category."}
          defaultOpen={false}
          testId="proc-section-booking"
        >
          <Field label="Suggest booking after">
            <div className="flex items-center gap-2">
              <input
                type="number" min="1" max="365" step="1"
                value={bookingWindowDays}
                onChange={(e) => setBookingWindowDays(e.target.value)}
                placeholder={
                  category && /^Endodontic|Oral Surgery$/.test(category) ? "5" :
                  category && /^(Restorative|Pediatric|Diagnostic|Orthodontic)$/.test(category) ? "10" :
                  category && /^Periodontal$/.test(category) ? "14" :
                  category && /^(Prosthodontic|Cosmetic|Implant)$/.test(category) ? "18" :
                  category && /^Preventive$/.test(category) ? "90" :
                  category && /^Emergency$/.test(category) ? "2" :
                  "10"
                }
                data-testid="proc-booking-window"
                className={`${DRAWER_CONTROL_CLASS} w-28`}
              />
              <span className="text-sm text-slate-500">days</span>
              <span className="text-xs text-slate-500">
                {bookingWindowDays !== "" ? "Clinic choice" : "Uses category default"}
              </span>
            </div>
            <p className="mt-1 text-xs leading-snug text-slate-500">
              Leave this blank to use the usual timing for the category. For multi-visit work, this also suggests the gap before the next visit.
            </p>
          </Field>
        </DisclosureSection>

        <DisclosureSection
          title="Codes and Ortho link"
          description="Optional. Most clinic staff will not need to change these."
          defaultOpen={!!orthoVisitType || category === "Orthodontic"}
          testId="proc-section-codes-ortho"
        >
          <div className="grid grid-cols-2 gap-3">
            <Field label="Short code">
              <input
                type="text" value={shortCode}
                onChange={(e) => setShortCode(e.target.value.toUpperCase())}
                placeholder="Example: OE"
                data-testid="proc-short-code"
                className={DRAWER_CONTROL_CLASS}
              />
            </Field>
            <Field label="Billing code">
              <input
                type="text" value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Optional"
                data-testid="proc-code"
                className={DRAWER_CONTROL_CLASS}
              />
            </Field>
          </div>

          <Field label="Use for this Ortho visit">
            <select
              value={orthoVisitType}
              onChange={(e) => setOrthoVisitType(e.target.value)}
              data-testid="proc-ortho-visit-type"
              className={DRAWER_CONTROL_CLASS}
            >
              {ORTHO_VISIT_LINK_OPTIONS.map((option) => {
                const owners = option.value ? orthoVisitLinkOwners.get(option.value) : null;
                return (
                  <option key={option.value || "none"} value={option.value}>
                    {option.label}{owners?.length ? ` (${owners.length} other services use this)` : ""}
                  </option>
                );
              })}
            </select>
            <p className="mt-1 text-xs leading-snug text-slate-500">
              Use this only for services booked as part of an Ortho case. The Ortho contract controls the charge.
            </p>
          </Field>
        </DisclosureSection>

        <Field label="Notes">
          <textarea
            rows={3} value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Internal notes. Patients will not see this."
            data-testid="proc-notes"
            className={DRAWER_CONTROL_CLASS}
          />
        </Field>

        {error ? (
          <div role="alert" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
            {error}
          </div>
        ) : null}
      </div>

      {/* Sticky footer */}
      <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-slate-100 bg-white">
        {isEdit && row.is_platform_default ? (
          <div className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500" data-testid="proc-core-stays-available">
            <LockKeyhole className="w-3.5 h-3.5" />
            Always available
          </div>
        ) : isEdit ? (
          <div>
            <button
              onClick={handleArchiveToggle} disabled={busy}
              data-testid="proc-archive-toggle"
              className="min-h-11 px-3 py-2 text-xs font-semibold border border-slate-200 rounded text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1.5"
            >
              {row.archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
              {row.archived ? "Offer again" : "Stop offering"}
            </button>
            <p className="text-[10px] text-slate-400 mt-1">Past records stay unchanged.</p>
          </div>
        ) : <span />}
        <div className="flex items-center gap-2">
          <button
            onClick={onCancel} disabled={busy}
            className="min-h-11 px-4 py-2 text-sm border border-slate-200 rounded text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave} disabled={busy || !canSave}
            data-testid="proc-save"
            className="min-h-11 px-5 py-2 text-sm font-semibold bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-40 inline-flex items-center gap-1.5"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isEdit ? "Save changes" : "Add service"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------
function DisclosureSection({ title, description, defaultOpen = false, testId, children }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <details
      className="group rounded-xl border border-slate-200 bg-white"
      open={isOpen}
      onToggle={(event) => setIsOpen(event.currentTarget.open)}
      data-testid={testId}
    >
      <summary className="flex min-h-14 cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 marker:hidden">
        <span>
          <span className="block text-sm font-semibold text-slate-800">{title}</span>
          <span className="mt-0.5 block text-xs leading-snug text-slate-500">{description}</span>
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-slate-400 transition-transform group-open:rotate-180" />
      </summary>
      <div className="space-y-3 border-t border-slate-100 px-4 py-4">
        {children}
      </div>
    </details>
  );
}

function normalizeVisitSteps(template) {
  if (!Array.isArray(template)) return [];
  return template.map((step, index) => ({
    ...(step && typeof step === "object" ? step : {}),
    label: String(step?.label || `Visit ${index + 1}`),
    chair_min: step?.chair_min == null ? "" : String(step.chair_min),
    lab_gap_days: step?.lab_gap_days == null ? "" : String(step.lab_gap_days),
  }));
}

function optionalWholeNumberIsValid(value, minimum, maximum) {
  if (value === "" || value == null) return true;
  const number = Number(value);
  return Number.isInteger(number) && number >= minimum && number <= maximum;
}

function serializeVisitSteps(steps) {
  return steps.map((step, index) => {
    const serialized = {
      ...step,
      label: String(step.label || `Visit ${index + 1}`).trim(),
    };
    if (step.chair_min === "" || step.chair_min == null) delete serialized.chair_min;
    else serialized.chair_min = Number(step.chair_min);
    if (step.lab_gap_days === "" || step.lab_gap_days == null) delete serialized.lab_gap_days;
    else serialized.lab_gap_days = Number(step.lab_gap_days);
    return serialized;
  });
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-700 mb-1">
        {label}{required ? <span className="text-rose-500"> *</span> : null}
      </label>
      {children}
    </div>
  );
}

function formatMoney(centavos, currency) {
  if (centavos == null) return "Depends on case";
  const pesos = (centavos || 0) / 100;
  const symbol = currency === "PHP" || !currency ? "₱" : currency + " ";
  return `${symbol}${pesos.toLocaleString("en-PH", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function centavosToPesoInput(centavos) {
  if (centavos == null) return "";
  return String(Math.round(Number(centavos || 0) / 100));
}

function pricePesosToCentavos(value) {
  const text = String(value ?? "").trim();
  if (text === "") return null;
  const pesos = Number(text);
  if (!Number.isFinite(pesos) || pesos < 0) return null;
  return Math.round(pesos) * 100;
}

function testIdSlug(value) {
  return String(value || "Uncategorized").replace(/[^A-Za-z0-9_-]+/g, "-");
}

function isPostOpProcedure(procedure) {
  const normalized = String(procedure?.name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
  return normalized === "post op" || normalized === "post op check" || procedure?.stable_id === "post_op";
}

function orthoVisitTypeLabel(value) {
  return ORTHO_VISIT_LINK_OPTIONS.find((option) => option.value === value)?.label || labelize(value);
}

function labelize(value) {
  return String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
